import { setSubscriptionFlags } from '@/data/flags/local-flags';
import { STARTER_2024_PACKAGE } from '@/free/free.constants.js';
import { getDaysRemainingInTrial } from '@/free/free.utils.js';
import { checkStagingServer } from '@/settings/environments/environment-settings.utils';
import { FY25_MODEL_STRING } from '@/settings/subscription-settings/utils';
import i18nDateTimeFormatter from '@/utils/i18n/i18n-datetime-formatter';
import { refreshLocale } from '@/utils/i18n/i18n.js';
import { setCurrentTimezoneForAppWideMoment } from '@/utils/moment-wrapper.ts';
import {
    getFeatureFlag,
    getTrialEndDate,
    subIsDefaultPlan,
    subIsInTrial,
} from '@/vuex/modules/subscription/subscription.helpers.js';
import {
    checkScimEnabledForSubscription,
    deleteSetting,
    extendFreeTrial,
    fetchEventCount,
    fetchSetting,
    fetchSubscription,
    getOIDCConfiguration,
    getSubscriptionVisitorMau,
    recordRaw,
    startFreeTrial,
    update,
    updateAutoGrantAccessExpiresOn,
    updateAutoProvFlag,
    updateFlag,
    updateInsightsFlag,
    updateSemanticSearchFlag,
    getSubscriptionPromptCount,
    updateOIDCConfiguration,
    updatePlan,
    updateSetting,
    updateSubscriptionExtensionAttributes, SUB_PROPERTY_API_BINDING_KEYS, updateByName,
    updateEmailThrottleSettings,
    updateJourneyThrottleSettings,
} from '@/vuex/modules/subscription/subscription.utils.js';
import find from 'lodash/find';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import Vue from 'vue';
import { retrieveMetadataByField } from '@/vuex/modules/metadata/metadata.utils';

export function getInitialState () {
    return {
        active: {},
        activeSubMauCount: -1,
        activeSubPromptCount: -1,
        allSubscriptions: {},
        currentMauLimit: -1,
        error: {},
        fetching: false,
        hasStagingServers: false,
        map: {},
        oidc: {},
        settings: {},
        updatingFlag: null,
        updatingProperty: null,
        vendorId: null,
    };
}

const subscription = {
    namespaced: true,
    state: getInitialState(),
    mutations: {
        setActive (state, { subscription }) {
            state.active = subscription;

            if (subscription.timeZone) {
                i18nDateTimeFormatter.timeZone = subscription.timeZone;
                setCurrentTimezoneForAppWideMoment(subscription.timeZone);
            }

            refreshLocale();
            setSubscriptionFlags(subscription.featureFlags);
        },
        setActiveSubMauCount (state, mauCount) {
            state.activeSubMauCount = mauCount;
        },
        setCurrentMauLimit (state, { newMauLimit }) {
            state.currentMauLimit = newMauLimit;
        },
        setActiveSubPromptCount (state, promptCount) {
            state.activeSubPromptCount = promptCount;
        },
        setActiveById (state, { id }) {
            state.active = state.map[id];

            if (state.active.timeZone) {
                i18nDateTimeFormatter.timeZone = state.active.timeZone;
                setCurrentTimezoneForAppWideMoment(state.active.timeZone);
            }

            refreshLocale();
            setSubscriptionFlags(state.active.featureFlags);
        },
        setMap (state, { map }) {
            state.map = map;
        },
        setSubById (state, { subscription }) {
            state.map[subscription.id] = subscription;
        },
        setHasStagingServers (state, { hasStagingServers }) {
            state.hasStagingServers = hasStagingServers;
        },
        setFetching (state, { fetching }) {
            state.fetching = fetching;
        },
        setSetting (state, { setting, value }) {
            Vue.set(state.settings, setting, value);
        },
        addSubToList (state, { subscription }) {
            Vue.set(state.map, subscription.id, subscription);
        },
        setError (state, { error }) {
            state.error = error;
        },
        setFlag (state, { flag, enabled, subscription }) {
            const targetSub = subscription || state.active;
            Vue.set(targetSub.featureFlags, flag, enabled);
            setSubscriptionFlags(targetSub.featureFlags);
        },
        setUpdatingFlag (state, { flag }) {
            state.updatingFlag = flag;
        },
        setProperty (state, { property, value }) {
            Vue.set(state.active, property, value);
            if (property === 'timeZone') {
                i18nDateTimeFormatter.timeZone = value;
                setCurrentTimezoneForAppWideMoment(value);
            }
        },
        setUpdatingProperty (state, { property }) {
            state.updatingProperty = property;
        },
        setOIDC (state, oidc) {
            state.oidc = oidc;
        },
        reset (state) {
            Object.assign(state, getInitialState());
        },
        setProductLines (state, { productLines }) {
            Vue.set(state.active, 'productLines', productLines);
        },
        setAllSubscriptions (state, { subscriptions }) {
            state.allSubscriptions = subscriptions;
        },
    },
    actions: {
        async hydrate ({ commit, dispatch, getters, rootGetters }, { subscriptions, activeSubscription }) {
            // Includes all AEU subs
            const allSubscriptions = subscriptions.reduce((acc, subscription) => {
                const { applications, productLines, ...rest } = subscription;
                acc[subscription.id] = { ...rest };

                return acc;
            }, {});

            commit('setAllSubscriptions', { subscriptions: allSubscriptions });

            let hasTrainingSub = false;
            // Only includes adopt on core AEU subs
            const subscriptionMapById = subscriptions.reduce((acc, subscription) => {
                const { applications, productLines, ...rest } = subscription;

                const isAeuNotOnCore = productLines.some((pl) => {
                    if (pl.type !== 'adoptEndUser') return false;
                    if (!pl.purchased) return false;

                    return pl.metadata.userInterface === 'adopt';
                });

                if (isAeuNotOnCore) {
                    hasTrainingSub = true;

                    return acc;
                }

                acc[subscription.id] = rest;

                return acc;
            }, {});

            commit('setMap', { map: subscriptionMapById });
            const activeSubIsAdoptUIAeu = activeSubscription.productLines.some((pl) => {
                if (pl.type !== 'adoptEndUser') return false;
                if (!pl.purchased) return false;

                return pl.metadata.userInterface === 'adopt';
            });

            // is this dumb? yes. we have a very weird edge where someone who has access to both AEU and normal pendo subs
            // but _doesn't_ have their active sub set on the BE can be placed into an AEU sub if it happens to be the first item
            // in their sub list. In this rare case, force a reload of the page to a valid sub. After we make some changes to /init
            // this can be deleted
            // also allow Ops users to do this for their convenience when they impersonate ⸜(｡˃ ᵕ ˂ )⸝♡
            const isOps = rootGetters['auth/isOps'];
            if (!isOps && activeSubIsAdoptUIAeu && getters.list.length) {
                window.location.href = `${window.location.origin}/s/${getters.list[0].id}/home`;

                return;
            }

            if (!getters.list.length) {
                dispatch('auth/updateOnlyTrainingSub', { onlyTrainingSub: hasTrainingSub }, { root: true });

                return;
            }

            commit('setActive', { subscription: activeSubscription });

            dispatch('getMauCount');
            dispatch('updateCurrentMauLimit');
            dispatch('fetchIsScimEnabled');

            // Fetch prompt count if agent analytics is purchased
            // Check directly from activeSubscription since productLines module may not be hydrated yet
            const agentAnalyticsPl = activeSubscription.productLines?.find((pl) => pl.type === 'agentAnalytics');
            if (agentAnalyticsPl?.purchased) {
                dispatch('getPromptCount');
            }
        },
        async loadActiveSubscription ({ commit, dispatch, state }) {
            const { eventCount, id, isScimEnabled } = state.active;
            try {
                // subs in the map contain properties not included in `fetchSubscription()`
                const activeSub = await fetchSubscription(id);
                const subscription = Object.assign({}, state.map[id], activeSub);

                // Deleted/missing values get re-introduced above; prevent that for these fields
                subscription.identifyStartingEventTime = activeSub.identifyStartingEventTime;
                subscription.pricingModel = activeSub.pricingModel;
                subscription.isScimEnabled = isScimEnabled;

                if (subscription.eventCount === 0 && eventCount > 0) {
                    subscription.eventCount = eventCount;
                }
                commit('setActive', { subscription });
                commit('setActiveSubMauCount', { mauCount: -1 });
                dispatch('getMauCount');
                const coreProductLine = subscription.productLines.find((productLine) => productLine.type === 'core');
                const subMauLimit = coreProductLine?.metadata?.mauLimit || -1;
                commit('setCurrentMauLimit', { newMauLimit: subMauLimit });

                // Fetch prompt count if agent analytics is purchased
                const agentAnalyticsPl = subscription.productLines?.find((pl) => pl.type === 'agentAnalytics');
                const hasAgentAnalytics = agentAnalyticsPl?.purchased;
                if (hasAgentAnalytics) {
                    dispatch('getPromptCount');
                }
            } catch (error) {
                commit('setError', { error });
            }
        },
        getHasStagingServers ({ commit }) {
            return checkStagingServer().then((hasStagingServers) => {
                commit('setHasStagingServers', { hasStagingServers });
            });
        },
        async loadSetting ({ commit, state }, { setting, reFetch = false, retrieveMetadata = true, abortSignal } = {}) {
            if (state.settings[setting] && !reFetch) {
                return;
            }

            commit('setError', { error: null });
            commit('setFetching', { fetching: true });

            try {
                const value = await fetchSetting(setting, abortSignal);
                if (value && retrieveMetadata) {
                    const [kind, group, field] = value.split('.');
                    const { data } = await retrieveMetadataByField(kind, group, field, abortSignal);
                    commit('setSetting', {
                        setting,
                        value: { value, label: data.DisplayName },
                    });
                } else if (value && !retrieveMetadata) {
                    commit('setSetting', {
                        setting,
                        value: { value },
                    });
                }
                commit('setFetching', { fetching: false });
            } catch (error) {
                commit('setError', { error });
                commit('setFetching', { fetching: false });
                throw error;
            }
        },
        async updateSetting ({ commit, state }, { setting, value, label }) {
            // Return if the setting already exists in the store and is identical to update
            if (state.settings[setting] && state.settings[setting].value === value) {
                return;
            }

            commit('setError', { error: null });
            commit('setFetching', { fetching: true });

            try {
                await updateSetting(setting, { value });
                commit('setSetting', { setting, value: { value, label } });
                commit('setFetching', { fetching: false });
            } catch (error) {
                commit('setError', { error });
                commit('setFetching', { fetching: false });
                throw error;
            }
        },
        async deleteSetting ({ commit }, { setting }) {
            commit('setFetching', { fetching: true });

            try {
                await deleteSetting(setting);
                commit('setSetting', { setting, value: null });
                commit('setFetching', { fetching: false });
            } catch (error) {
                commit('setError', { error });
                commit('setFetching', { fetching: false });
                throw error;
            }
        },
        async updateFlag ({ commit, dispatch, getters }, { flag, enabled, optimistic = false }) {
            const originalValue = getters.getFeatureFlag(flag);
            if (optimistic) {
                commit('setFlag', { flag, enabled });
            }
            commit('setUpdatingFlag', { flag });
            try {
                await updateFlag({ flag, enabled });
                await dispatch('loadActiveSubscription');
                if (!optimistic) {
                    commit('setFlag', { flag, enabled });
                }
                commit('setUpdatingFlag', { flag: null });
            } catch (error) {
                if (optimistic) {
                    commit('setFlag', { flag, enabled: originalValue });
                }
                commit('setError', { error });
                commit('setUpdatingFlag', { flag: null });
                throw error;
            }
        },
        async updateAutoProvFlag ({ commit, dispatch }, { id, reason }) {
            try {
                await updateAutoProvFlag({ subscriptionId: id, reason });
                await dispatch('loadActiveSubscription');
                commit('setFlag', { flag: 'autoProvisioned', enabled: false });
                commit('setUpdatingFlag', { flag: null });
            } catch (error) {
                commit('setError', { error });
                commit('setUpdatingFlag', { flag: null });
                throw error;
            }
        },
        async updateProperty ({ state, commit }, { property, value }) {
            commit('setUpdatingProperty', { property });
            try {
                const bindingKey = SUB_PROPERTY_API_BINDING_KEYS[property];
                if (bindingKey) {
                    await updateByName(property, { [bindingKey]: value });
                } else {
                    await update({ ...state.active, [property]: value });
                }
                commit('setProperty', { property, value });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setUpdatingProperty', { property: null });
            }
        },
        async startRecordingRawEvents ({ commit }) {
            try {
                const { recordUntil } = await recordRaw();
                commit('setProperty', {
                    property: 'recordUntil',
                    value: recordUntil,
                });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async updateEventCount ({ commit }) {
            try {
                const eventCount = await fetchEventCount();
                commit('setProperty', {
                    property: 'eventCount',
                    value: eventCount,
                });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async startFreeTrial ({ commit }, { duration = 14, checklist, upfront } = {}) {
            try {
                const subscription = await startFreeTrial({ duration, checklist, upfront });
                commit('setActive', { subscription });
                commit('setSubById', { subscription });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async getMauCount ({ commit, getters }) {
            try {
                const { featureFlagList, isFY25PricingModel } = getters;

                const mauOnly = featureFlagList.includes('mauOnly');
                const showAnonymous = featureFlagList.includes('showAnonymous');

                const mauCount = await getSubscriptionVisitorMau({ mauOnly, showAnonymous, isFY25PricingModel });
                commit('setActiveSubMauCount', mauCount);
            } catch (error) {
                commit('setError', { error });
            }
        },
        setPromptCount ({ commit }, promptCount) {
            commit('setActiveSubPromptCount', promptCount);
        },
        async getPromptCount ({ commit }) {
            try {
                const promptCount = await getSubscriptionPromptCount();
                commit('setActiveSubPromptCount', promptCount);
            } catch (error) {
                commit('setError', { error });
            }
        },
        async extendFreeTrial ({ commit }, { numDays, subscriptionId }) {
            try {
                const subscription = await extendFreeTrial(numDays, subscriptionId);
                commit('setActive', { subscription });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async fetchOIDC ({ commit }) {
            try {
                const settings = await getOIDCConfiguration();
                commit('setOIDC', settings);
            } catch (error) {
                commit('setError', { error });
                commit('setOIDC', { clientId: '', trustedIssuer: '' });
            }
        },
        async updateOIDC ({ commit }, { body }) {
            const response = await updateOIDCConfiguration({
                ...body,
            });
            commit('setOIDC', response);
        },
        async updateAutoGrantAccessExpiresOn ({ commit, dispatch }, { property, value }) {
            commit('setUpdatingProperty', { property });
            try {
                await updateAutoGrantAccessExpiresOn({ property, value });
                await dispatch('loadActiveSubscription');
                commit('setProperty', { property, value });
                commit('setUpdatingProperty', { property: null });
            } catch (error) {
                commit('setError', { error });
                commit('setUpdatingProperty', { property: null });
                throw error;
            }
        },
        async updateCurrentMauLimit ({ state, commit, rootGetters }) {
            try {
                if (rootGetters['auth/isAuthenticated'] && !rootGetters['auth/hasOnlyTrainingSub']) {
                    const activeSubId = state.active.id;
                    const subscription = await fetchSubscription(activeSubId);
                    const coreProductLine = subscription.productLines.find(
                        (productLine) => productLine.type === 'core',
                    );
                    const subMauLimit = coreProductLine?.metadata?.mauLimit || -1;

                    commit('setCurrentMauLimit', { newMauLimit: subMauLimit });
                }
            } catch (error) {
                commit('setError', { error });
            }
        },
        addSubToList ({ commit }, { subscription }) {
            commit('addSubToList', { subscription });
        },
        updateProductLines ({ commit }, { productLines }) {
            commit('setProductLines', { productLines });
        },
        async updatePlan (
            { state, commit, dispatch },
            { plan, isUpgrade, sfdcAccountId, sfdcAccountName, downgradeMethod = 'immediate', query },
        ) {
            const planBody = { plan };
            if (isUpgrade) {
                if (!sfdcAccountId) {
                    throw new Error('Upgrading a plan requires a SFDC Account ID');
                }
                planBody.sfdcAccountId = sfdcAccountId;
                if (sfdcAccountName) planBody.sfdcAccountName = sfdcAccountName;
            } else {
                planBody.downgradeMethod = downgradeMethod;
            }

            try {
                await updatePlan(planBody, query);
                await dispatch('loadActiveSubscription');
                dispatch('apps/loadAll', { reFetch: true }, { root: true });

                const { productLines } = state.active;
                commit('productLines/setMap', { map: keyBy(productLines, 'type') }, { root: true });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async updateExtensionAttributes ({ state, commit }, { extensionAttributes }) {
            const currentExtensionAttributes = state.active.extensionAttributes;
            const newExtensionAttributes = Object.assign({}, currentExtensionAttributes, extensionAttributes);

            try {
                const response = await updateSubscriptionExtensionAttributes(newExtensionAttributes);
                commit('setProperty', { property: 'extensionAttributes', value: response });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async updateInsightsFlag ({ commit, dispatch }, { set }) {
            commit('setUpdatingFlag', { flag: 'enableMlChurnInsights' });
            try {
                await updateInsightsFlag({ set });
                await dispatch('loadActiveSubscription');
                commit('setUpdatingFlag', { flag: null });
            } catch (error) {
                commit('setError', { error });
                commit('setUpdatingFlag', { flag: null });
                throw error;
            }
        },
        async updateSemanticSearchFlag ({ commit, dispatch }, { set }) {
            commit('setUpdatingFlag', { flag: 'entityEmbedding' });
            try {
                await updateSemanticSearchFlag({ set });
                await dispatch('loadActiveSubscription');
                commit('setUpdatingFlag', { flag: null });
            } catch (error) {
                commit('setError', { error });
                commit('setUpdatingFlag', { flag: null });
                throw error;
            }
        },
        async fetchIsScimEnabled ({ state, commit }, { reFetch = false } = {}) {
            if ('isScimEnabled' in state.active && !reFetch) {
                return;
            }

            try {
                const isScimEnabled = await checkScimEnabledForSubscription();
                commit('setProperty', { property: 'isScimEnabled', value: isScimEnabled });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        setFlag ({ commit }, { flag, enabled }) {
            commit('setFlag', { flag, enabled });
        },
        async saveEmailThrottleSettings ({ commit, state }, { settings }) {
            const { data } = await updateEmailThrottleSettings(settings);
            commit('setProperty', {
                property: 'emailSettings',
                value: { ...state.active.emailSettings, throttlesettings: data },
            });

            return data;
        },
        async saveJourneyThrottleSettings ({ commit, state }, { settings }) {
            const { data } = await updateJourneyThrottleSettings(settings);
            commit('setProperty', {
                property: 'journeySettings',
                value: { ...state.active.journeySettings, throttlesettings: data },
            });

            return data;
        },
        async ensureSubscriptionEmailThrottleSettingsLoaded ({ dispatch, state }) {
            const hasLoaded = get(state.active, 'emailSettings.throttlesettings');
            if (!hasLoaded) {
                await dispatch('loadActiveSubscription');
            }
        },
        async ensureSubscriptionJourneyThrottleSettingsLoaded ({ dispatch, state }) {
            const hasLoaded = get(state.active, 'journeySettings.throttlesettings');
            if (!hasLoaded) {
                await dispatch('loadActiveSubscription');
            }
        },
    },
    getters: {
        allSubscriptions: (state) => Object.values(state.allSubscriptions),
        defaultSubscription: (state, getters) =>
            find(state.map, (sub) => sub.id !== get(getters.sampleDataSubscription, 'id')),
        daysRemainingInTrial: (state, getters) =>
            getDaysRemainingInTrial(getters.getActiveSub?.trialEndDate || 0, getters.isInAutoProvisionedTrial),
        hadUpfrontTrial: (state, getters) => getters.getFeatureFlag('hadUpfrontTrial'),
        isEligibleForTrial: (state, getters) => {
            return getters.getActiveSub?.trialEndDate === 0 && getters.isPendoFree;
        },
        isEligibleForPreInstallTrial: (state, getters) => {
            return getters.getActiveSub?.trialEndDate === 0 && !getters.snippetInstalled && getters.isPendoFree;
        },
        canStartTrial: (state, getters) => {
            return getters.isEligibleForTrial && getters.isPendoFree;
        },
        canUpgrade (state, getters, rootState, rootGetters) {
            return (
                getters.isPendoFree &&
                getters.purchasableSelfServicePlan === STARTER_2024_PACKAGE.planId &&
                rootGetters['auth/hasSegmentFlag']('AccountHqInIndia')
            );
        },
        isFreeAndNotIndia (state, getters, rootState, rootGetters) {
            return (
                getters.isPendoFree &&
                !(
                    getters.purchasableSelfServicePlan === STARTER_2024_PACKAGE.planId &&
                    rootGetters['auth/hasSegmentFlag']('AccountHqInIndia')
                )
            );
        },
        purchasableSelfServicePlan (state, getters) {
            return getters.getActiveSub?.purchasableSelfServicePlan;
        },
        subscriptionPackageTiers: (state, getters) => {
            return {
                isPendoFree: getters.isPendoFree,
                isInTrial: getters.isInTrial,
                isStarterPackage: getters.isStarterPackage,
                isStarter2024Package: getters.isStarter2024Package,
                isEligibleForTrial: getters.isEligibleForTrial,
                canStartTrial: getters.canStartTrial,
                daysRemainingInTrial: getters.daysRemainingInTrial,
                canUpgrade: getters.canUpgrade,
                purchasableSelfServicePlan: getters.purchasableSelfServicePlan,
            };
        },
        // used by pendo snippet
        featureFlagList: (state) => {
            const { featureFlags } = state.active;
            if (!featureFlags) {
                return;
            }

            return Object.keys(featureFlags).filter((flag) => featureFlags[flag]);
        },
        trialHistory: (state, getters) => getters.getActiveSub?.trialHistory,
        isInTrial: (state) => subIsInTrial(state.active),
        isInAutoProvisionedTrial: (state, getters) => {
            // auto-provisioned trials do NOT use sub.isInTrial - that field is reserved for PLG trials
            // "in trial" = autoProvisioned feature flag and non-zero sub.trialEndDate
            // "paid" = autoProvisioned feature flag and sub.trialEndDate === 0
            const hasFeatureFlag = getters.getFeatureFlag('autoProvisioned');
            const hasNonZeroTrialEndDate = state.active.trialEndDate !== 0;

            return hasFeatureFlag && hasNonZeroTrialEndDate;
        },
        isAutoProvisionedTrialExpired: (state, getters) => {
            const isActiveTrial = getters.isInAutoProvisionedTrial;
            const isExpired = state.active.trialEndDate < Date.now();

            return isActiveTrial && isExpired;
        },
        isStarterPackage: (state, getters) => getters.getFeatureFlag('starterPackage') && !getters.isPendoFree,
        isStarter2024Package: (state, getters) => getters.getFeatureFlag('starter2024Package') && !getters.isPendoFree,
        isFreeOrStarter: (state, getters) =>
            getters.isPendoFree || getters.isStarterPackage || getters.isStarter2024Package,
        isDefaultPlan: (state) => subIsDefaultPlan(state.active),
        isPartnerAnalytics: (state, getters) => getters.getFeatureFlag('partnerAnalytics'),
        trialEndDate: (state) => getTrialEndDate(state.active),
        getActiveSub: (state) => state.active,
        getFeatureFlag: (state) => (flag) => getFeatureFlag(state.active, flag),
        getSubFeatureFlag: () => (subscription, flag) => !!get(subscription, `featureFlags.${flag}`, false),
        hasV2RolesAndPermissionsWithFeatureArea: (state, getters) =>
            getters.getFeatureFlag('enableRolesAndPermissionV2') &&
            // extend with || on other feature area permissioning flags
            (getters.getFeatureFlag('enableGuidePermissionV2') ||
                getters.getFeatureFlag('enableTaggingPermissionV2') ||
                getters.getFeatureFlag('enableSharingPermissionV2')),
        isAdoptOnCore: (state, getters, rootState, rootGetters) => {
            const aeuPl = rootGetters['productLines/productLineByType']('adoptEndUser');

            return aeuPl.purchased && aeuPl.metadata.userInterface === 'engage';
        },
        isAdoptEndUser: (state, getters, rootState, rootGetters) => {
            return rootGetters['productLines/hasProductLinePurchased']('adoptEndUser');
        },
        isTrainingSubscription: (state, getters) => getters.isAdoptEndUser,
        isAdoptReseller: (state, getters, rootState, rootGetters) => {
            return rootGetters['productLines/hasProductLinePurchased']('adoptReseller');
        },
        isActiveTrainingPartner: (state, getters) => getters.isAdoptReseller,
        isActiveViaPartner: (state, getters) => getters.isAdoptReseller,
        isDigitalAdoptionOnly: (state, getters, rootState, rootGetters) => {
            const p4eAdoptPl = rootGetters['productLines/productLineByType']('adopt');

            return p4eAdoptPl.purchased && p4eAdoptPl.metadata.userInterface === 'adopt';
        },
        hasVisualDesignStudioEnabled: (state, getters, rootState, rootGetters) => {
            const oneUiPl = rootGetters['productLines/productLineByType']('oneUIEngineeringUse');

            return oneUiPl.purchased && oneUiPl.metadata.visualDesignStudioEnabled;
        },
        hasExtensionEnabled: (state, getters, rootState, rootGetters) => {
            const oneUiPl = rootGetters['productLines/productLineByType']('oneUIEngineeringUse');

            return oneUiPl.purchased && oneUiPl.metadata.extensionApplicationEnabled;
        },
        isPendoFree: (state, getters) => getters.getFeatureFlag('pendoFree') && !getters.isInTrial,
        isMemberOfSubscription: (state) => (id) => !!state.map[id],
        list: (state) => Object.values(state.map),
        mauCount: (state) => state.activeSubMauCount,
        mauLimit: (state) => state.currentMauLimit,
        loadingMauData: (state) => {
            return state.currentMauLimit === -1 || state.mauCount === -1;
        },
        isOverMauLimit: (state, getters) => {
            // Return false if they are in a trial or not having MAU enforced
            if (!getters.getFeatureFlag('enforceMauLimiting') || getters.isInTrial || state.currentMauLimit === -1) {
                return false;
            }

            return state.activeSubMauCount > state.currentMauLimit;
        },
        promptCount: (state) => state.activeSubPromptCount,
        promptLimit: (state, getters, rootState, rootGetters) => {
            const agentAnalyticsPl = rootGetters['productLines/productLineByType']('agentAnalytics');
            const nestedLimit = get(agentAnalyticsPl, 'metadata.agentAnalyticsProductLineMetadata.monthlyPromptLimit');
            const flatLimit = get(agentAnalyticsPl, 'metadata.monthlyPromptLimit');

            return nestedLimit ?? flatLimit ?? -1;
        },
        hasAgentAnalyticsPurchased: (state, getters, rootState, rootGetters) => {
            return rootGetters['productLines/hasProductLinePurchased']('agentAnalytics');
        },
        isOverPromptLimit: (state, getters) => {
            if (getters.isInTrial || getters.promptLimit === -1) {
                return false;
            }

            return state.activeSubPromptCount >= getters.promptLimit;
        },
        isFreeAndOverMauLimit: (state, getters) => getters.isPendoFree && getters.isOverMauLimit,
        requireSSO: (state, getters) =>
            getters.getFeatureFlag('requireGoogleSSO') ||
            getters.getFeatureFlag('requireSAML') ||
            getters.getFeatureFlag('requireFeedbackSAML'),
        sampleDataSubscription: (state) => find(state.map, (sub) => sub.featureFlags.sampleDataSubscription) || null,
        eventRate: (state) => get(state, 'active.eventRate', 1),
        isUpdatingAutoGrantAccessExpiresOn: (state) => state.updatingProperty === 'autoGrantAccessExpiresOn',
        hasAIGeneratedThemes: (state, { getFeatureFlag }) =>
            getFeatureFlag('enableNPSThemesAssignment') && getFeatureFlag('enableNPSThemesTriage'),
        hasNPSLLM: (state, { getFeatureFlag }) => getFeatureFlag('enableNPSLLM'),
        hasInsights: (state, { getFeatureFlag }) =>
            getFeatureFlag('enableMlChurnInsights') && getFeatureFlag('enableMlAnalysis'),
        snippetInstalled: (state, getters, rootState, rootGetters) =>
            state.active.eventCount > 0 || rootGetters['apps/mobileList'].some((app) => app.integrated),
        setupKey: (state) => state.active.setupKey,
        hasSessionReplayEnabled: (state, getters, rootState, rootGetters) => {
            return rootGetters['productLines/hasProductLinePurchased']('sessionReplay');
        },
        hasExtensionAppCodeBlocksEnabled: (state, getters, rootState, rootGetters) => {
            return rootGetters['productLines/hasProductLinePurchased']('guides', 'extensionAppCustomCodeBlocksEnabled');
        },
        mauHashCutoff: (state) => state.active.mauHashCutoff,
        hasAccountLevelAnalyticsEnabled: (state, getters) => {
            return getters.getFeatureFlag('enableAccounts');
        },
        hasIdentityManagementEnabled: (state) => {
            return !!get(state.active, 'identifyStartingEventTime', false);
        },
        isFY25PricingModel: (state) => {
            return state.active?.pricingModel === FY25_MODEL_STRING || false;
        },
        isScimEnabled: (state) => {
            return get(state.active, 'isScimEnabled', false);
        },
        subLocale: (state) => {
            return get(state.active, 'locale');
        },
        hasTaggingScreenshotsEnabled: (state, getters) => {
            return !getters.getFeatureFlag('disableScreenshots');
        },
        trainingAttributes: (state) => {
            return get(state.active, 'trainingAttributes', {});
        },
        minVisitorsInSegment: (state) => {
            return get(state.active, 'minVisitorsInSegment', 0);
        },
        getSubscriptionEmailThrottleSettings: (state) => {
            return get(state.active, 'emailSettings.throttlesettings', {});
        },
        getSubscriptionJourneyThrottleSettings: (state) => {
            return get(state.active, 'journeySettings.throttlesettings', {});
        },
    },
};

export default subscription;
